import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";

import { useBillingNavigation } from "./hooks/useBillingNavigation";
import { useResponsiveCardWidth } from "./hooks/useResponsiveCardWidth";
import { useScrollStripTabs } from "./hooks/useScrollStripTabs";
// import { useUser } from "routes/dataroutes/UserData"; // ENG-1416: unused while Free is hidden
import { useAllPlans } from "routes/data-routes/billing-data/plans";
import { usePlanHelpers } from "routes/data-routes/billing-data/plans/usePlanHelpers";
import { useNextHigherPlan } from "v2/hooks/billing/pricing-options/useNextHigherPlan";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import ModalCloseButton from "v2/components/billing/shared/ModalCloseButton";
import PlanCardV2 from "v2/components/billing/PlanCardV2";
import EnterprisePlanCard from "v2/components/billing/EnterprisePlanCard";
import CoreFeaturesSection from "v2/components/billing/PricingTable/CoreFeaturesSection";
import CoreGrowthSegmentedControl from "v2/components/billing/CoreGrowthSegmentedControl";
import AnnualBillingToggle from "v2/components/billing/AnnualBillingToggle";
import PlanTabSwitchArrow from "v2/components/billing/PlanTabSwitchArrow";
import { fmtCurrencyCompact, fmtShortNumber } from "utils";
import { getPlanTier, isLegacyAnnualPlan, isYearlyPlan } from "utils/planUtils";
import Plan from "types/Plan";
import { ChargebeePlanCategory } from "types/PlanCustomFields";
import { trackBillingV2ContactUsClicked, trackBillingV2ModalOpened, trackBillingV2ViewAllPlansViewport, } from "utils/Tracking";
import DomainMultipliersText from "components/common/DomainMultipliers";


// Promotional bonus credits by tier. Yearly multiplies by 12; the label shows the monthly figure.
function planBonusCredits(planSlug: string, isYearly: boolean) {
  const tier = getPlanTier(planSlug);
  const monthly = tier === "professional" ? 250_000 : tier === "advanced" ? 500_000 : 0;
  return {
    periodBonusCredits: isYearly ? monthly * 12 : monthly,
    limitedOfferText: monthly > 0 ? `LIMITED OFFER · +${fmtShortNumber(monthly)} BONUS CREDITS` : undefined,
  };
}


export default function ViewAllPlansContentV2({ onClose }: { onClose: () => void }) {
  // const user = useUser();
  const { navigate } = useBillingNavigation();
  const { allPlans } = useAllPlans();
  const { getPlanChangeKind } = usePlanHelpers();
  const { pricingOptions } = usePricingOptions();
  const autoUpgradeEnabled = pricingOptions?.["auto-upgrade"]?.enabled ?? false;
  const { nextHigherPlan } = useNextHigherPlan();
  const { activeSubscription } = useActiveSubscription();

  const currentPlanSlug = activeSubscription?.planSlug;
  const scheduledPlanSlug = activeSubscription?.scheduledSubscription?.planSlug;

  const [isYearly, setIsYearly] = useState(true);
  const scrollerRef = useRef<HTMLDivElement>(null);

  // ENG-1416: Free plan temporarily hidden from the pricing table for everyone.
  const showFreePlan = false;
  // isBannedFromFree users can't complete a downgrade to Free, so don't offer the card.
  // const showFreePlan = currentPlanSlug !== "extended_trial"
  //   && currentPlanSlug !== "standard_extended_trial"
  //   && !user?.isBannedFromFree;

  useEffect(() => {
    trackBillingV2ModalOpened('view_all_plans');
    trackBillingV2ViewAllPlansViewport();
  }, []);

  const handleContactSalesClick = () => {
    trackBillingV2ContactUsClicked('view_all_plans_modal');
    navigate("contact-sales");
  };

  const isNonMonthly = (planSlug: string | undefined) =>
    isYearlyPlan(planSlug) || isLegacyAnnualPlan(planSlug);

  const isDisplayedPlanCurrent = (displayedPlan: Plan) => {
    if (!currentPlanSlug) return false;
    const sameTier = getPlanTier(currentPlanSlug) === getPlanTier(displayedPlan.planSlug);
    return sameTier && isNonMonthly(currentPlanSlug) === isNonMonthly(displayedPlan.planSlug);
  };

  const yearlyFilter = useCallback((plan: Plan) => {
    return isYearly ? isYearlyPlan(plan.planSlug) : !(isYearlyPlan(plan.planSlug) || isLegacyAnnualPlan(plan.planSlug));
  }, [ isYearly ]);

  // One plan per MRR group for the category (the Core/Growth tabs), honouring the monthly/yearly toggle.
  const selectPlans = useCallback((category: ChargebeePlanCategory) => {
    return (allPlans ?? [])
      .map(group => group.find(plan => plan.category === category && yearlyFilter(plan)))
      .filter((plan): plan is Plan => plan !== undefined);
  }, [ allPlans, yearlyFilter ]);

  const corePlans = useMemo(
    () => selectPlans("Core").filter(plan => showFreePlan || plan.planSlug !== "free"),
    [ selectPlans, showFreePlan ],
  );
  const growthPlans = useMemo(() => selectPlans("Growth"), [ selectPlans ]);

  // No grandfathering: switching plans always moves users onto the newest ones.
  const plansToShow = useMemo(() => _.compact([ ...corePlans, ...growthPlans ]), [ corePlans, growthPlans ]);
  const coreCount = corePlans.length;

  const { cardWidth, cardGap } = useResponsiveCardWidth(scrollerRef, isYearly, !!allPlans);
  const { activeTab, scrollToTab, switchToOtherTab } = useScrollStripTabs(scrollerRef, coreCount);

  if (!allPlans) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">Loading plans...</p>
      </div>
    );
  }

  // Cards from the group you're not on (peeking at the edge) are dimmed to keep focus on the
  // active group; they stay scrollable and clickable.
  const groupShadowClass = (idx: number) => {
    const inActiveGroup = activeTab === "Core" ? idx < coreCount : idx >= coreCount;
    return `transition-opacity duration-200 ${inActiveGroup ? "" : "opacity-40"}`;
  };

  const renderPlanCard = (plan: Plan, idx: number) => {
    const originalMonthlyPriceLabel = (isYearly && plan?.price) ? fmtCurrencyCompact(plan.price / 100 / 12, plan.currencyCode) : undefined;
    const { periodBonusCredits, limitedOfferText } = planBonusCredits(plan.planSlug, isYearly);
    return (
      <div key={plan.planSlug} className={`flex-shrink-0 snap-start ${groupShadowClass(idx)}`} style={{ width: cardWidth }}>
        <PlanCardV2
          plan={plan}
          isCurrentPlan={isDisplayedPlanCurrent(plan)}
          recommendedPlanSlug={ nextHigherPlan?.planSlug }
          isDowngrade={ getPlanChangeKind(currentPlanSlug, plan.planSlug) === "downgrade" }
          scheduledPlanSlug={scheduledPlanSlug}
          autoUpgradeEnabled={autoUpgradeEnabled}
          originalMonthlyPriceLabel={originalMonthlyPriceLabel}
          isYearly={isYearly}
          limitedOfferText={limitedOfferText}
          bonusCredits={periodBonusCredits}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col px-8 pt-6 pb-6 max-w-[1400px] mx-auto w-full">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-primary-900">Choose your plan</h2>
        <ModalCloseButton onClick={onClose} />
      </div>

      <div className="flex flex-wrap gap-4 items-center justify-between mb-6">
        <CoreGrowthSegmentedControl activeTab={activeTab} onChange={scrollToTab} />
        <AnnualBillingToggle isYearly={isYearly} onChange={setIsYearly} />
      </div>

      <div className="relative mb-4">
        <PlanTabSwitchArrow
          direction={activeTab === "Core" ? "right" : "left"}
          onClick={switchToOtherTab}
        />
        <div
          ref={scrollerRef}
          className="flex items-stretch overflow-x-auto scroll-smooth snap-x snap-proximity py-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          style={{ gap: cardGap }}
        >
          {plansToShow.map(renderPlanCard)}
          <div className={`flex-shrink-0 snap-start ${groupShadowClass(plansToShow.length)}`} style={{ width: cardWidth }}>
            <EnterprisePlanCard
              onContactSalesClick={handleContactSalesClick}
              isYearly={isYearly}
            />
          </div>
        </div>
      </div>

      <DomainMultipliersText />

      <CoreFeaturesSection />
    </div>
  );
}
